import { useEffect } from 'react';
import { useWorldStore } from '../state/worldStore';
import { resolveLevel, forgetLevel, resolveStory, forgetStory } from '../services/access';
import { COME_UP } from '../config';

// AccessBoot — works out which rung the viewer is on, once, and puts it in the
// store. No UI.
//
// Separate from the gate on purpose. `enterInterior` has to answer instantly —
// it is inside a movement trigger — so it can only ever read an answer somebody
// else already fetched. This is that somebody.
//
// ── WHY IT RE-RESOLVES ON EMBODIMENT ─────────────────────────────────────────
// The top rung is "has a body", and a body belongs to the avatar you are
// currently appearing as rather than to the account. Switching who you are
// wearing genuinely changes the answer, so it is a real dependency and not
// belt-and-braces.
export default function AccessBoot() {
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  const setAccessLevel = useWorldStore((s) => s.setAccessLevel);
  const setStoryGate = useWorldStore((s) => s.setStoryGate);
  // Re-read the story gate every time they come back out onto the street.
  // Walking out of the salon is exactly when the answer changes, and the studio
  // door is very often the next thing they touch.
  const interior = useWorldStore((s) => s.interior);

  useEffect(() => {
    let dead = false;
    forgetLevel();
    resolveLevel(myAvatarId)
      .then((level) => { if (!dead) setAccessLevel(level); })
      // Never leave it null. Null means "not known yet" and every gate defers to
      // it, so a failure that left it null would quietly open every door in the
      // world rather than closing them.
      .catch(() => { if (!dead) setAccessLevel('everyone'); });
    return () => { dead = true; };
  }, [myAvatarId, setAccessLevel]);

  useEffect(() => {
    // NO EARLY RETURN ON A MISSING AVATAR. This bailed before the resolver ran,
    // so an unidentified session never got a gate at all — and ?gate=lock never
    // even got read, which is why forcing the door shut appeared to do nothing.
    // "No avatar" is an answer the gate knows how to give; let it give it.
    let dead = false;
    forgetStory();
    // No story door while the come-up is hidden (config.COME_UP): the studio
    // answers to the access ladder like every other room. Null = no gate.
    if (!COME_UP) { setStoryGate(null); return () => { dead = true; }; }
    resolveStory(myAvatarId).then((st) => { if (!dead) setStoryGate(st); });
    return () => { dead = true; };
  }, [myAvatarId, setStoryGate, interior]);

  return null;
}
